import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginRequestSchema } from '@evi/shared';
import type { LoginRequest } from '@evi/shared';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { login, API_BASE_URL } from '@/lib/api';
import { loginErrorCopy } from '@/lib/authError';
import { isMfaPending, setMfaFlow } from '@/lib/mfaFlow';
import { useAuthStore } from '@/store/auth';
import { AuthLayout } from '@/components/AuthLayout';
// Shared form rules extracted in AAP.3 (.form, .field, .label,
// .input, .submitBtn, .btnSecondary, .error, .serverError,
// .formSuccess). LoginPage-specific rules (.passwordWrapper,
// .eyeBtn, .forgotRow, .forgotLink, .infoBanner, .divider) stay
// in LoginPage.module.css.
import formStyles from '@/components/AuthForm.module.css';
import styles from './LoginPage.module.css';

// Re-use the shared Zod schema — single source of truth for validation
type LoginForm = LoginRequest;

interface LoginPageProps {
  onSuccess: () => void;
  /** AAP.2 — needed to navigate from the magic-link button to the
   *  request form. App.tsx threads its `navigate` here. The Google
   *  + Apple SSO stubs still call handleSsoComingSoon since those
   *  flows aren't built yet. */
  onNavigate: (path: string) => void;
}

/** ?email= from an invite to an existing account (its sign-in page link); empty otherwise. */
function invitedEmail(): string {
  try {
    const email = new URLSearchParams(window.location.search).get('email')?.trim() ?? '';
    return email.length <= 254 && /^[^\s@]+@[^\s@]+$/.test(email) ? email : '';
  } catch { return ''; }
}

export function LoginPage({ onSuccess, onNavigate }: LoginPageProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const setAuth = useAuthStore((s) => s.setAuth);

  // The Google OAuth backend bounces failed or rejected admin
  // sign-ins (denied consent, expired state, unknown email, non-admin
  // role) to /login?error=google. Surface the generic copy in the
  // existing serverError banner — deliberately generic so a probing
  // email can't learn whether an account exists — and strip the param
  // so a refresh doesn't re-show it. The admin shell has no toast
  // store; the banner IS its pattern.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    if (params.get('error') === 'google') {
      setServerError("Google sign-in didn't complete, give it another try");
      params.delete('error');
      changed = true;
    } else if (params.get('error') === 'google_link') {
      // The first Google link to a never-verified password account was
      // refused because the notice email is not deliverable (google.ts).
      // Point at the two sign-ins that DO work rather than "try again".
      setServerError(
        'An account with this email already has a password. Sign in with it, or email yourself a magic link.',
      );
      params.delete('error');
      changed = true;
    }
    // Involuntary sign-out: the server refused the refresh (cookie expired,
    // or revoked, for example by a sign-out elsewhere), so api.ts sent us
    // here with ?reason=expired and a cause. There is no idle timeout, so
    // the notice names no cause. The cause only marks this page load's URL
    // for diagnosis; strip it with the reason.
    if (params.get('reason') === 'expired') {
      setNotice('Your session ended. Please sign in again.');
      params.delete('reason');
      params.delete('cause');
      changed = true;
    }
    if (changed) {
      const qs = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  }, []);
  // Default to dark mode for the admin surface (founder direction
  // reversal — the earlier "force light on login" rule has been
  // dropped). The ThemeProvider at packages/skins/src/ThemeProvider.tsx:96
  // already falls back to 'dark' when no localStorage preference
  // exists; without the override below the login page reads in
  // dark for fresh users and in whatever-the-user-picked for
  // returning users.
  //
  // No explicit setMode/setThemeId call here. Stored preferences
  // win; the admin's first paint is dark for new sessions; the
  // /login surface no longer re-applies a forced override every
  // time it mounts.
  //
  // useTheme() removed since we don't read or mutate the theme
  // here anymore. If a future commit needs to surface a
  // theme-toggle on the login page itself, re-import it then.

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginRequestSchema),
    // An invite to an existing account links here with its email (29 October
    // 2026, the invite says sign in with it): filled in, so they only add the password.
    defaultValues: { email: invitedEmail() },
  });

  const onSubmit = async (data: LoginForm) => {
    setServerError(null);
    try {
      const res = await login(data);
      // MFA branch (dormant until commit 5 arms the server): when
      // the response is the pending shape instead of a session,
      // stash the 5-minute token in the in-memory flow state and
      // route to the matching screen. The normal session path below
      // is unchanged for the non-MFA case.
      if (isMfaPending(res)) {
        setMfaFlow(res);
        onNavigate(res.variant === 'enroll' ? '/auth/mfa/enroll' : '/auth/mfa/challenge');
        return;
      }
      setAuth(res.user, res.access_token);
      onSuccess();
    } catch (err) {
      // Branded, status-mapped copy — never the raw transport error (which
      // leaks the API URL, method, and status code into the banner). The one
      // exception is an answer that comes after the password matched and says
      // what to do (an invite waiting on this mailbox, loginErrorCopy).
      setServerError(
        await loginErrorCopy(err, "That email and password don't match. Try again or use a magic link."),
      );
    }
  };

  return (
    <AuthLayout
      title="Admin login"
      tagline="Charging station management"
      subtitle="Sign in to your admin account"
    >
        {/* Involuntary-signout notice, from api.ts's ?reason=expired
            redirect. Top of the surface so it is the first thing seen. */}
        {notice && (
          <div className={styles.infoBanner} role="status">
            {notice}
          </div>
        )}

        {/* Entry order leads with the no-password paths, matching the
            driver PWA: magic link first, Google second, then the
            email/password form below the divider. AuthLayout's .body
            carries a 16px column gap, so the buttons keep their
            spacing outside the form element. */}
        <button
          type="button"
          className={formStyles.btnSecondary}
          onClick={() => onNavigate('/auth/magic')}
        >
          <Sparkles size={16} aria-hidden /> Email me a magic link
        </button>

        {/* Backend-initiated Google OAuth, admin surface — a FULL-PAGE
            navigation (not window.open: the redirect chain leaves and
            returns the page). surface=admin rides the signed state, so
            the callback applies the admin rule set: find-only, admin
            roles only, never auto-create. Failures and rejections
            bounce back here as /login?error=google, surfaced by the
            mount effect above. */}
        <button
          type="button"
          className={formStyles.btnSecondary}
          onClick={() => { window.location.href = `${API_BASE_URL}/auth/google?surface=admin`; }}
        >
          <GoogleIcon /> Continue with Google
        </button>

        <div className={styles.divider}>or</div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className={formStyles.form}>
          {/* Email */}
          <div className={formStyles.field}>
            <label htmlFor="email" className={formStyles.label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`${formStyles.input} ${errors.email ? formStyles.inputError : ''}`}
              placeholder="admin@evinitiative.com"
              {...register('email')}
            />
            {errors.email && <p className={formStyles.error}>{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div className={formStyles.field}>
            <label htmlFor="password" className={formStyles.label}>
              Password
            </label>
            <div className={styles.passwordWrapper}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className={`${formStyles.input} ${styles.passwordInput} ${errors.password ? formStyles.inputError : ''}`}
                placeholder="••••••••••••"
                {...register('password')}
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <p className={formStyles.error}>{errors.password.message}</p>}
          </div>

          {/* Forgot password link — AAP.3 fixed the path from
              /auth/forgot-password (404) to /auth/forgot (wired
              in App.tsx's unauthenticated branch). Uses
              onNavigate for proper SPA routing instead of the
              previous anchor-tag full-page-load behaviour. */}
          <div className={styles.forgotRow}>
            <button
              type="button"
              className={styles.forgotLink}
              onClick={() => onNavigate('/auth/forgot')}
            >
              Forgot your password?
            </button>
          </div>

          {/* Server error banner — real auth failures plus the
              Google-callback bounce read by the mount effect. */}
          {serverError && (
            <div className={formStyles.serverError} role="alert">
              {serverError}
            </div>
          )}

          {/* Submit */}
          <button type="submit" className={formStyles.submitBtn} disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>

        </form>
        <p><a href="/guides/evi-v3-onboarding.html" target="_blank" rel="noreferrer">Getting started? Choose your onboarding guide</a></p>
    </AuthLayout>
  );
}

/* Brand-color Google "G" — same SVG used on the driver login
   so the two surfaces match pixel-for-pixel. */
function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 5.04c1.61 0 3.05.56 4.18 1.66l3.13-3.13C17.46 1.74 14.97.66 12 .66 7.43.66 3.49 3.27 1.7 7l3.66 2.83c.86-2.6 3.27-4.79 6.64-4.79z" />
      <path fill="#34A853" d="M23.34 12.27c0-.79-.07-1.55-.2-2.27H12v4.51h6.43c-.28 1.5-1.13 2.78-2.4 3.62l3.69 2.86c2.16-2 3.62-4.93 3.62-8.72z" />
      <path fill="#4A90E2" d="M5.36 14.21c-.22-.66-.34-1.36-.34-2.08s.12-1.42.34-2.08L1.7 7.21C.97 8.66.55 10.29.55 12s.42 3.34 1.15 4.79l3.66-2.58z" />
      <path fill="#FBBC05" d="M12 23.34c2.97 0 5.46-.98 7.28-2.66l-3.69-2.86c-1.02.69-2.34 1.1-3.59 1.1-3.37 0-6.22-2.2-7.24-5.13l-3.66 2.58c1.79 3.73 5.73 6.97 10.9 6.97z" />
    </svg>
  );
}

